import { average, splitByRanges, uniqueValues } from '@/pure/Array';
import SortedArray from '@/pure/SortedArray';

const timeRanges = [0, 10, 20, 30, 40, 50, 60];
export const timeRangesCaptions = ['0-10min', '11-20min', '21-30min', '31-40min', '41-50min', '51-60min', '+60min'];

const extendedTimeRanges = [0, 10, 20, 30, 40, 50, 60, 90, 120];
export const extendedTimeRangesCaptions = ['0-10min', '11-20min', '21-30min', '31-40min', '41-50min', '51-60min', '61-90min', '91-120min', '+120min'];

const getTimeRanges = useExtendedTimeRanges => (useExtendedTimeRanges ? extendedTimeRanges : timeRanges);

const sensibleTimeRanges = [20, 25, 30];

class ScreenData {
  reduceData(collaborators, study, globalFilters) {
    if (!study) return;

    const useExtendedTimeRanges = study.maximum_travel_time_enabled !== null && study.maximum_travel_time_enabled !== undefined;
    const { currentMatchingTime } = globalFilters;

    const flatActualTimes = collaborators
      .map(item => {
        const siteId = item.details.actualSiteID;
        const actualSite = item.sites.find(site => site.id.toString() === siteId.toString());

        if (actualSite) {
          const transportType = this.getCurrentMatchingTime(currentMatchingTime, item, actualSite.id);

          if (transportType !== 'none') return actualSite.times[0][transportType];
        }

        return 0;
      })
      .filter(c => c);

    const averageTime = average(flatActualTimes);

    const environmentalImpact =
      collaborators.reduce((acc, person) => {
        if (!person.sites || person.sites.length === 0) return acc;

        const site = person.sites.find(site => site.id.toString() === person.details.actualSiteID.toString());

        if (!site) return 0;

        const matcher = this.getCurrentMatchingTime(currentMatchingTime, person, site.id);
        return acc + (site.times[0][`${matcher}_co2`] || 0);
      }, 0) / 1000;

    const environmentalImpactWeighted =
      collaborators.reduce((acc, person) => {
        if (!person.sites || person.sites.length === 0) return acc;

        const site = person.sites.find(site => site.id.toString() === person.details.actualSiteID.toString());

        if (!site) return 0;

        const matcher = this.getCurrentMatchingTime(currentMatchingTime, person, site.id);

        const co2 = (site.times[0][`${matcher}_co2`] || 0) * ((person.details.days || 5) / 5);

        return acc + co2;
      }, 0) / 1000;

    const sortedCollaborators = new SortedArray(study.collaborators, (a, b) => {
      if (a.id === b.id) return 0;
      return a.id < b.id ? -1 : 1;
    });

    const collaboratorsByRegion = this.computedByRegion(collaborators, uniqueValues(collaborators.map(item => item.details.region)), person =>
      this.getActualTime(sortedCollaborators, person, currentMatchingTime),
    );

    const regions = collaboratorsByRegion
      .sort((a, b) => {
        return b.percentage - a.percentage;
      })
      .map(a => a.caption);

    const sites = this.computeByTargetSites(study, collaborators, averageTime, regions, globalFilters, useExtendedTimeRanges);

    const actualSites = this.computeByActualSites(study, collaborators, globalFilters);

    const collaboratorsByTimeRanges = splitByRanges(flatActualTimes, getTimeRanges(useExtendedTimeRanges)).map(item => {
      return {
        averageTime: average(item),
        count: item.length,
      };
    });

    return {
      averageTime,
      collaboratorsCount: study.collaborators.length,
      filteredCollaboratorsCount: collaborators.length,
      filteredCollaborators: collaborators,
      unrecognizedCount: study.collaborators.filter(item => item.address === null).length,
      offLimitCount: study.collaborators.filter(item => item.sites.length <= 0).length,
      excludedAddresses: study.excluded_addresses ? study.excluded_addresses : 0,
      collaboratorsByTimeRanges,
      collaboratorsByRegion,
      environmentalImpact,
      environmentalImpactWeighted,
      sites,
      actualSites,
      creationDate: study.creation_date,
      date: study.date,
      travelType: study.travel_type,
      travelTimeType: study.travel_time_type,
      clientName: study.client_name,
      name: study.name,
      currentTransportFilter: globalFilters.currentMatchingTime,
      useExtendedTimeRanges,
    };
  }

  getCurrentMatchingTime(currentMatchingTime, person, siteId) {
    if (currentMatchingTime === 'custom') {
      return person.details.transportType;
    } else if (currentMatchingTime === 'optimized') {
      return this.matchPersonSite(person, siteId).optimizedSite;
    } else {
      return currentMatchingTime;
    }
  }

  matchPersonSite(person, siteId) {
    return person.sites.find(site => site.id === siteId);
  }

  getCollaboratorsByMatchingTime(collaborators, site, currentMatchingTime) {
    const persons = [];
    let i;

    for (i = 0; i < collaborators.length; i++) {
      const collaborator = collaborators[i];
      const currentSite = this.matchPersonSite(collaborator, site.id);

      if (currentSite) {
        const matcher = this.getCurrentMatchingTime(currentMatchingTime, collaborator, currentSite.id);

        if (currentSite.times[0][matcher]) {
          const currentPerson = {
            ...collaborator,
            siteTime: currentSite.times[0][matcher],
            co2: currentSite.times[0][`${matcher}_co2`] || 0,
          };
          delete currentPerson.sites;
          persons.push(currentPerson);
        }
      }
    }
    return persons;
  }

  computedByRegion(collaborators, regions, mapper) {
    const countryRegions = collaborators.reduce((acc, el) => {
      acc[el.details['region']] = el.details.country;
      return acc;
    }, {});

    const regionsData = regions.map(region => {
      let entries = collaborators.filter(person => person.details.region === region);
      entries = !entries ? [] : entries.map(mapper).filter(e => !isNaN(e));

      return {
        caption: region,
        country: countryRegions[region],
        count: entries.length,
        averageTime: average(entries),
        percentage: (entries.length / collaborators.length) * 100,
        rawPercentage: (entries.length / collaborators.length) * 100,
      };
    });

    const diff = 100 - regionsData.map(e => Math.floor(e.percentage)).reduce((acc, val) => acc + val, 0);
    const sortedRegionsData = regionsData.sort((a, b) => Math.floor(a.percentage) - a.percentage - (Math.floor(b.percentage) - b.percentage));

    return sortedRegionsData.map((e, index) => {
      const percentage = index < diff ? Math.floor(e.percentage) + 1 : Math.floor(e.percentage);
      return { ...e, percentage };
    });
  }

  computeByTargetSites(study, collaborators, averageTime, regions, filters, useExtendedTimeRanges) {
    const { currentMatchingTime } = filters;

    const sites = study.sites ? study.sites : [];
    let targetSitesData = [];

    let i;
    let index = 0;
    for (i = 0; i < sites.length; i++) {
      const site = { ...sites[i], index: index };

      if (site.type === 'Target') {
        index += 1;
      }
      if (site.type === 'Target' && filters.targetSites && filters.targetSites.find(item => item === site.id)) {
        const sitedCollaborators = this.getCollaboratorsByMatchingTime(collaborators, site, currentMatchingTime);

        const environmentalImpact =
          sitedCollaborators.reduce((acc, person) => {
            return acc + (person.co2 || 0);
          }, 0) / 1000;

        const environmentalImpactWeighted =
          sitedCollaborators.reduce((acc, person) => {
            return acc + (person.co2 * ((person.details.days || 5) / 5) || 0);
            // return acc + (person.co2 * (Math.round((Math.random() * 4) + 1) / 5) || 0)
          }, 0) / 1000;

        let actualTimes = [];
        let currentTimes = [];

        let j;
        const decayedCollaborators = [];
        const improvedCollaborators = [];
        const sensibleCaseCollaborators = [];

        const sortedCollaborators = new SortedArray(study.collaborators, (a, b) => {
          if (a.id === b.id) return 0;
          return a.id < b.id ? -1 : 1;
        });

        for (j = 0; j < sitedCollaborators.length; j++) {
          const person = sitedCollaborators[j];
          const actualTime = this.getActualTime(sortedCollaborators, person, currentMatchingTime);

          actualTimes.push(actualTime);
          currentTimes.push(person.siteTime);

          if (person.siteTime > actualTime) {
            decayedCollaborators.push(person);
          } else {
            improvedCollaborators.push(person);
          }

          if (person.siteTime - actualTime >= 20 && person.siteTime >= (study.sensitive_case_duration || 45)) {
            sensibleCaseCollaborators.push(person);
          }
        }

        const collaboratorsByTimeRanges = splitByRanges(currentTimes, getTimeRanges(useExtendedTimeRanges)).map(item => {
          return {
            averageTime: average(item),
            count: item.length,
          };
        });

        const decayedCollaboratorsTimes = [];
        const decayedCollaboratorsTimesDiff = [];

        for (j = 0; j < decayedCollaborators.length; j++) {
          const person = decayedCollaborators[j];

          decayedCollaboratorsTimes.push(person.siteTime);
          decayedCollaboratorsTimesDiff.push(person.siteTime - this.getActualTime(sortedCollaborators, person, currentMatchingTime));
        }

        const decayedCollaboratorsTimesByTimeRanges = splitByRanges(decayedCollaboratorsTimesDiff, getTimeRanges(useExtendedTimeRanges)).map(item => {
          return {
            count: item.length,
            averageTime: average(item),
            averageTimeDiff: average(decayedCollaboratorsTimesDiff),
          };
        });

        const improvedCollaboratorsTimes = [];
        const improvedCollaboratorsTimesDiff = [];

        for (j = 0; j < improvedCollaborators.length; j++) {
          const person = improvedCollaborators[j];

          improvedCollaboratorsTimes.push(person.siteTime);
          improvedCollaboratorsTimesDiff.push(Math.abs(person.siteTime - this.getActualTime(sortedCollaborators, person, currentMatchingTime)));
        }

        const improvedCollaboratorsTimesByTimeRanges = splitByRanges(improvedCollaboratorsTimesDiff, getTimeRanges(useExtendedTimeRanges)).map(item => {
          return {
            count: item.length,
            averageTime: average(item),
            averageTimeDiff: average(improvedCollaboratorsTimesDiff),
          };
        });

        let sensibleCaseCollaboratorsTimes = [];
        let sensibleCaseCollaboratorsTimesDiff = [];

        for (j = 0; j < sensibleCaseCollaborators.length; j++) {
          const person = sensibleCaseCollaborators[j];
          sensibleCaseCollaboratorsTimes.push(person.siteTime);
          sensibleCaseCollaboratorsTimesDiff.push(person.siteTime - this.getActualTime(sortedCollaborators, person, currentMatchingTime));
        }
        const sensibleCaseCollaboratorsByTimeRanges = splitByRanges(sensibleCaseCollaboratorsTimes, sensibleTimeRanges).map(item => {
          return {
            averageTime: average(item),
            count: item.length,
          };
        });
        const sensibleCaseCollaboratorsByTimeDiffRanges = splitByRanges(sensibleCaseCollaboratorsTimesDiff, sensibleTimeRanges).map(item => {
          return {
            averageTime: average(item),
            count: item.length,
          };
        });
        const collaboratorsByRegion = this.computedByRegion(collaborators, regions, person => {
          const currentSite = this.matchPersonSite(person, site.id);
          if (!currentSite) return;

          const matcher = this.getCurrentMatchingTime(currentMatchingTime, person, site.id);
          return currentSite.times[0][matcher];
        });

        const sensibleCollaboratorsByRegion = this.computedByRegion(sensibleCaseCollaborators, regions, person => person.siteTime);

        const impact = {
          improvement: {
            count: improvedCollaborators.length,
            averageTime: average(improvedCollaboratorsTimes),
            averageTimeDiff: average(improvedCollaboratorsTimesDiff),
          },
          decay: {
            count: decayedCollaboratorsTimes.length,
            averageTime: average(decayedCollaboratorsTimes),
            averageTimeDiff: average(decayedCollaboratorsTimesDiff),
          },
          sensibleCaseDecay: {
            count: sensibleCaseCollaboratorsTimes.length,
            averageTime: average(sensibleCaseCollaboratorsTimes),
            averageTimeDiff: average(sensibleCaseCollaboratorsTimesDiff),
          },
        };

        site.details = {
          decayedCollaboratorsTimesByTimeRanges,
          improvedCollaboratorsTimesByTimeRanges,
          sensibleCaseCollaboratorsByTimeRanges,
          sensibleCaseCollaboratorsByTimeDiffRanges,
          sensibleCaseCollaborators,
          sensibleCollaboratorsByRegion,
          collaboratorsByTimeRanges,
          collaboratorsByRegion,
          environmentalImpact,
          environmentalImpactWeighted,
          averageTime: average(currentTimes),
          averageTimeDiff: average(currentTimes) - average(actualTimes),
          impact,
        };

        targetSitesData.push(site);
      }
    }

    return targetSitesData;
  }

  getActualSite(collaborator) {
    const actualSite = collaborator.sites.find(s => s.id.toString() === collaborator.details.actualSiteID.toString());

    if (actualSite) {
      return actualSite;
    }
    return null;
  }

  getActualTime(collaborators, person, currentMatchingTime) {
    const i = collaborators.search(person);
    const fullCollaborator = collaborators.array[i];

    const matcher = this.getCurrentMatchingTime(currentMatchingTime, fullCollaborator, parseInt(person.details.actualSiteID));

    return this.getActualSite(fullCollaborator).times[0][matcher];
  }

  computeByActualSites(study, collaborators, filters) {
    return study.sites
      ? study.sites
          .filter(site => site.type === 'Actual')
          .map(site => {
            const sitedCollaborators = this.getCollaboratorsByMatchingTime(collaborators, site, filters.currentMatchingTime);
            const currentTimes = sitedCollaborators.map(person => person.siteTime);
            site.details = {
              averageTime: average(currentTimes),
            };
            return site;
          })
      : [];
  }
}

export default ScreenData;
